/** Contact hours: time zone + start/end. Anything scheduled outside waits for the next window. */
import { useId } from 'react';
import { TIMEZONES, type ContactHours, type SettingsErrors } from './types';

const inputClass =
  'mt-1 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900 aria-[invalid=true]:border-danger';

export function ContactHoursEditor({
  value,
  errors,
  onChange,
}: {
  value: ContactHours;
  errors: SettingsErrors['contact_hours'];
  /** `touched` names the field an edit changed so its inline error can clear at once. */
  onChange: (next: ContactHours, touched?: keyof ContactHours) => void;
}) {
  const id = useId();
  const zones = TIMEZONES.some((z) => z.value === value.tz) ? TIMEZONES : [...TIMEZONES, { value: value.tz, label: value.tz }];
  // The error sits NEXT TO the <label>, not inside it: a wrapping label would fold the
  // message into the field's accessible name ("Until End must be after start.") and
  // repeat it through aria-describedby (QA stage 3).
  const field = (key: 'start' | 'end', label: string) => {
    const err = errors?.[key];
    return (
      <div className="text-[13px]">
        <label htmlFor={`${id}-${key}`} className="font-medium text-gray-800">
          {label}
        </label>
        <input
          id={`${id}-${key}`}
          type="time"
          value={value[key]}
          step={900}
          onChange={(e) => onChange({ ...value, [key]: e.target.value }, key)}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-${key}-err` : undefined}
          className={inputClass}
        />
        {err && (
          <span id={`${id}-${key}-err`} role="alert" className="mt-1 block text-[12px] text-danger">
            {err}
          </span>
        )}
      </div>
    );
  };

  return (
    <section aria-labelledby={`${id}-h`} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <h2 id={`${id}-h`} className="text-[15px] font-semibold text-gray-900">Contact hours</h2>
      <p className="mt-1 text-[13px] text-muted">
        Texts and calls go out only between these times. Anything due outside waits for the next window.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
        <div className="text-[13px]">
          <label htmlFor={`${id}-tz`} className="font-medium text-gray-800">
            Time zone
          </label>
          <select
            id={`${id}-tz`}
            value={value.tz}
            onChange={(e) => onChange({ ...value, tz: e.target.value }, 'tz')}
            aria-invalid={errors?.tz ? true : undefined}
            aria-describedby={errors?.tz ? `${id}-tz-err` : undefined}
            className={inputClass}
          >
            {zones.map((z) => (
              <option key={z.value} value={z.value}>{z.label}</option>
            ))}
          </select>
          {errors?.tz && (
            <span id={`${id}-tz-err`} role="alert" className="mt-1 block text-[12px] text-danger">
              {errors.tz}
            </span>
          )}
        </div>
        {field('start', 'From')}
        {field('end', 'Until')}
      </div>
    </section>
  );
}
